import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { RootState } from 'store/reducers';
import { downloadDocument } from 'store/actions/assetActions';
import {
  EditZVRProcess,
  RegistrationZVRProcess,
  RevokeHCPRegistrationModal,
} from 'modules/zvr/components';
import { Button } from 'modules/common/components';

import { QUESTIONNAIRE_KEYS } from 'modules/assistant/constants/questionnaires';

import { ReactComponent as RevokeIcon } from 'images/icon-person-revoke.svg';

interface ZvrButtonsProps {
  asset: AssetDTO;
}

export default function ZvrButtons({ asset }: ZvrButtonsProps) {
  const { t } = useTranslation(['plan']);
  const dispatch = useDispatch();

  const assistantProgress = useSelector(
    (state: RootState) => state.assistant.progress
  );
  const [openEditZVRProcess, setOpenEditZVRProcess] = React.useState(false);
  const [openRevokeRegistrationModal, setOpenRevokeRegistrationModal] =
    React.useState(false);
  const [openRegistrationProcess, setOpenRegistrationProcess] =
    React.useState(false);
  const [isDownloading, setIsDownloading] = React.useState(false);

  const isRegistered = asset.zvr === 1;

  const handleRegister = (event) => {
    event.stopPropagation();
    setOpenRegistrationProcess(true);
  };

  const handleUpdateTrustedPersons = () => {
    setOpenEditZVRProcess(true);
  };

  const handleRevoke = () => {
    setOpenRevokeRegistrationModal(true);
  };
  const handleEditZVRProcessClose = () => {
    setOpenEditZVRProcess(false);
  };

  const handleRevokeRegistrationModalClose = () => {
    setOpenRevokeRegistrationModal(false);
  };

  const handleCloseRegisterProcess = () => {
    setOpenRegistrationProcess(false);
  };

  const handleDocumentDownload = async () => {
    setIsDownloading(true);
    if (asset?.documents.length) {
      await dispatch(downloadDocument(asset?.documents[0]));
    }
    setIsDownloading(false);
  };

  const getChildren = () => {
    // if the document is registered -> show ZVR HCP card with Edit and Revoke features
    if (isRegistered) {
      return (
        <>
          <Button
            className={`t-mx-auto t-w-full t-my-4`}
            onClick={handleUpdateTrustedPersons}
          >
            {t('zvrRegistration:hcp_card_update_btn')}
          </Button>
          <button
            className={`button t-mx-auto t-w-full t-text-zeta-600 t-mb-4 t-bg-eta-100`}
            onClick={handleRevoke}
          >
            <RevokeIcon className="t-mr-2" />
            {t('zvrRegistration:hcp_card_revoke_btn')}
          </button>
        </>
      );
      // HCP exist but not registered ->  open register modal
    } else if (assistantProgress.healthCareProxy?.status === 1) {
      return (
        <>
          <Button
            className={`t-mx-auto t-w-full t-my-4`}
            onClick={handleRegister}
          >
            {t('zvrRegistration:hcp_card_register_btn')}
          </Button>
        </>
      );
    }
  };

  const children = getChildren();

  return (
    <>
      {openEditZVRProcess && (
        <EditZVRProcess onCancelProcess={handleEditZVRProcessClose} />
      )}
      {openRevokeRegistrationModal && (
        <RevokeHCPRegistrationModal
          onClose={handleRevokeRegistrationModalClose}
        />
      )}
      {openRegistrationProcess && (
        <RegistrationZVRProcess
          onCancelProcess={handleCloseRegisterProcess}
          handleDocumentDownload={handleDocumentDownload}
          downloading={isDownloading}
          questionnaire={QUESTIONNAIRE_KEYS.HEALTH_CARE_PROXY}
          isFromLegacyPlan
        />
      )}
      {children}
    </>
  );
}
